import React, {useMemo, useState, useEffect, useCallback} from 'react';
import {motion, AnimatePresence} from 'framer-motion';
import {useFinancial} from '../context/FinancialContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ReactECharts from 'echarts-for-react';

const {
  FiTrendingUp, FiDollarSign, FiBox, FiPlus, FiTrash2, FiActivity, FiX, FiRefreshCw,
  FiPieChart, FiSliders, FiCalendar, FiArrowUp, FiArrowDown, FiMinus, FiCreditCard,
  FiAlertCircle, FiCheckCircle, FiEdit2, FiCheck
} = FiIcons;

const CATEGORY_MAP = {
  mutualFunds: 'Equity',
  stocks: 'Equity',
  crypto: 'Equity',
  pms: 'Equity',
  fixedDeposits: 'Debt',
  bonds: 'Debt',
  epfPpf: 'Debt',
  ssyNsc: 'Debt',
  chitFund: 'Debt',
  bankSavings: 'Debt',
  emergencyFund: 'Debt',
  gold: 'Commodity',
  silver: 'Commodity',
  copper: 'Commodity',
  realEstate: 'Commodity',
  vehicles: 'Other',
  others: 'Other'
};

const CATEGORY_COLORS = {
  Equity: {
    bg: 'bg-blue-500',
    light: 'bg-blue-50',
    text: 'text-blue-600',
    border: 'border-blue-200',
    hex: '#3b82f6'
  },
  Debt: {
    bg: 'bg-emerald-500',
    light: 'bg-emerald-50',
    text: 'text-emerald-600',
    border: 'border-emerald-200',
    hex: '#10b981'
  },
  Commodity: {
    bg: 'bg-amber-500',
    light: 'bg-amber-50',
    text: 'text-amber-600',
    border: 'border-amber-200',
    hex: '#f59e0b'
  },
  Other: {
    bg: 'bg-gray-400',
    light: 'bg-gray-50',
    text: 'text-gray-500',
    border: 'border-gray-200',
    hex: '#6b7280'
  }
};

const DEFAULT_RETURNS = {
  Equity: 12,
  Debt: 7,
  Commodity: 8,
  Other: 5
};

const formatCurrency = (value) => new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0
}).format(value || 0);

const formatLakh = (value) => {
  const abs = Math.abs(value || 0);
  if (abs >= 10000000) return `₹${(value / 10000000).toFixed(2)}Cr`;
  if (abs >= 100000) return `₹${(value / 100000).toFixed(2)}L`;
  return formatCurrency(value);
};

// ─── CATEGORY RETURN BADGE ───────────────────────────────────────────────────
function ReturnBadge({value}) {
  return (
    <span className="text-xs font-bold text-gray-400 italic">
      Avg. {Number(value).toFixed(1)}% p.a.
    </span>
  );
}

// ─── CHANGE INDICATOR ────────────────────────────────────────────────────────
function ChangeIndicator({current, original}) {
  const diff = current - original;
  if (Math.abs(diff) < 0.1) return <span className="text-xs text-gray-400 font-bold">No change</span>;
  const isUp = diff > 0;
  return (
    <span className={`flex items-center text-xs font-black ${isUp ? 'text-blue-600' : 'text-red-500'}`}>
      {isUp ? <FiArrowUp className="mr-0.5" /> : <FiArrowDown className="mr-0.5" />}
      {Math.abs(diff).toFixed(1)}%
    </span>
  );
}

// ─── ASSET EDIT MODAL ────────────────────────────────────────────────────────
function AssetEditModal({asset, onClose, onSave}) {
  const [editData, setEditData] = useState({
    category: asset.category,
    expectedReturn: asset.expectedReturn
  });

  const handleSave = () => {
    onSave(asset.id, editData);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={onClose}>
      <motion.div
        initial={{scale: 0.9, opacity: 0}}
        animate={{scale: 1, opacity: 1}}
        className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-8">
          <h3 className="text-xl font-black text-gray-900 uppercase">Edit Asset</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-all"
          >
            <SafeIcon icon={FiX} />
          </button>
        </div>

        <div className="space-y-6">
          {/* Asset Name (Read-only) */}
          <div className="space-y-1">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Asset Name</label>
            <div className="w-full px-4 py-3 bg-gray-100 border border-gray-200 rounded-xl font-bold text-gray-600">
              {asset.name}
            </div>
          </div>

          {/* Current Value (Read-only) */}
          <div className="space-y-1">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Current Value</label>
            <div className="w-full px-4 py-3 bg-gray-100 border border-gray-200 rounded-xl font-bold text-gray-600">
              {formatCurrency(asset.value)}
            </div>
          </div>

          {/* Category Selection */}
          <div className="space-y-1">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Category</label>
            <select
              value={editData.category}
              onChange={(e) => setEditData({...editData, category: e.target.value})}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl font-bold outline-none focus:ring-2 focus:ring-[#19b48d]"
            >
              <option value="Equity">Equity</option>
              <option value="Debt">Debt</option>
              <option value="Commodity">Commodity</option>
              <option value="Other">Other</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Current: {asset.category} → New: {editData.category}
            </p>
          </div>

          {/* Expected Return */}
          <div className="space-y-1">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Expected Return (% p.a.)</label>
            <input
              required
              type="number"
              step="0.1"
              value={editData.expectedReturn}
              onChange={(e) => setEditData({...editData, expectedReturn: parseFloat(e.target.value) || 0})}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl font-bold outline-none focus:ring-2 focus:ring-[#19b48d]"
              placeholder="e.g., 12.5"
            />
            <p className="text-xs text-gray-500 mt-1">
              Current: {asset.expectedReturn}% → New: {editData.expectedReturn}%
            </p>
          </div>

          {/* Future Value Preview */}
          <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
            <p className="text-xs font-bold text-blue-800 uppercase mb-2">10-Year Projection Preview</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-blue-700">Current Settings</p>
                <p className="text-lg font-black text-blue-900">
                  {formatLakh(asset.value * Math.pow(1 + asset.expectedReturn / 100, 10))}
                </p>
              </div>
              <div>
                <p className="text-xs text-green-700">New Settings</p>
                <p className="text-lg font-black text-green-900">
                  {formatLakh(asset.value * Math.pow(1 + editData.expectedReturn / 100, 10))}
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-4 bg-gray-100 text-gray-700 font-black rounded-xl hover:bg-gray-200 transition-all uppercase tracking-widest"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="flex-1 py-4 bg-[#19b48d] text-white font-black rounded-xl shadow-lg shadow-[#19b48d]/20 hover:bg-[#16a085] transition-all uppercase tracking-widest flex items-center justify-center gap-2"
            >
              <SafeIcon icon={FiCheck} />
              Save Changes
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
function InvestmentPlanning() {
  const {state, dispatch} = useFinancial();
  const {
    userProfile,
    investmentPlanning: {returns},
    manualAssets,
    manualLiabilities,
    assetOverrides
  } = state;

  const [showAssetModal, setShowAssetModal] = useState(false);
  const [showLiabilityModal, setShowLiabilityModal] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);

  // ── Build unified asset list ────────────────────────────────────────────
  const unifiedAssets = useMemo(() => {
    const assets = [];
    const sourceAssets = {
      ...userProfile.existingInvestments,
      ...userProfile.assets,
      emergencyFund: userProfile.emergencyFund
    };

    Object.entries(sourceAssets).forEach(([key, value]) => {
      const val = Number(value) || 0;
      if (val > 0) {
        const name = key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase());
        const defaultCategory = CATEGORY_MAP[key] || 'Other';
        
        // Check if user has overridden category or return
        const overrides = assetOverrides[key] || {};
        const category = overrides.category || defaultCategory;
        const expectedReturn = overrides.expectedReturn ?? (returns[key] || DEFAULT_RETURNS[category]);

        assets.push({
          id: key,
          isManual: false,
          name,
          category,
          value: val,
          expectedReturn,
          originalCategory: defaultCategory
        });
      }
    });

    manualAssets.forEach(a => assets.push({
      id: a.id,
      isManual: true,
      name: a.name,
      category: a.category,
      value: Number(a.value) || 0,
      expectedReturn: Number(a.expectedReturn) || 0
    }));

    return assets;
  }, [userProfile, returns, manualAssets, assetOverrides]);

  // ── Handle Asset Edit Save ──────────────────────────────────────────────
  const handleAssetEditSave = (assetId, newData) => {
    const asset = unifiedAssets.find(a => a.id === assetId);
    
    if (asset.isManual) {
      // Update manual asset
      const updatedManualAssets = manualAssets.map(a =>
        a.id === assetId
          ? {...a, category: newData.category, expectedReturn: newData.expectedReturn}
          : a
      );
      dispatch({
        type: 'UPDATE_MANUAL_ASSETS',
        payload: updatedManualAssets
      });
    } else {
      // Update asset override
      dispatch({
        type: 'UPDATE_ASSET_OVERRIDE',
        payload: {
          key: assetId,
          overrides: {
            category: newData.category,
            expectedReturn: newData.expectedReturn
          }
        }
      });
    }
  };

  // ── Totals ───────────────────────────────────────────────────────────────
  const totalAssets = unifiedAssets.reduce((s, a) => s + a.value, 0);
  const totalLiabilities = Object.values(userProfile.liabilities).reduce((s, l) => s + (Number(l) || 0), 0) +
    manualLiabilities.reduce((s, l) => s + (Number(l.value) || 0), 0);
  const currentNetWorth = totalAssets - totalLiabilities;

  // ── Weighted returns per category ────────────────────────────────────────
  const categoryReturns = useMemo(() => {
    const cats = {Equity: [], Debt: [], Commodity: [], Other: []};
    unifiedAssets.forEach(a => cats[a.category].push({v: a.value, r: a.expectedReturn}));
    const weighted = {};
    Object.keys(cats).forEach(cat => {
      const total = cats[cat].reduce((s, x) => s + x.v, 0);
      weighted[cat] = total === 0 ? DEFAULT_RETURNS[cat] : cats[cat].reduce((s, x) => s + x.r * (x.v / total), 0);
    });
    return weighted;
  }, [unifiedAssets]);

  // ── Current (real) allocations ─────────────────────────────────────────
  const currentAllocations = useMemo(() => {
    const allocs = {Equity: 0, Debt: 0, Commodity: 0, Other: 0};
    if (totalAssets === 0) return {Equity: 25, Debt: 25, Commodity: 25, Other: 25};
    unifiedAssets.forEach(a => {
      allocs[a.category] = (allocs[a.category] || 0) + (a.value / totalAssets) * 100;
    });
    return allocs;
  }, [unifiedAssets, totalAssets]);

  // ── Simulated allocations state ──────────────────────────────────────────
  const [simAllocations, setSimAllocations] = useState({...currentAllocations});
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setSimAllocations({...currentAllocations});
    setHasChanges(false);
  }, [currentAllocations]);

  // ── Proportional slider handler ──────────────────────────────────────────
  const handleSliderChange = useCallback((changedCat, rawValue) => {
    const newValue = Math.min(100, Math.max(0, parseFloat(rawValue)));
    const otherCats = Object.keys(simAllocations).filter(c => c !== changedCat);
    const currentOtherTotal = otherCats.reduce((s, c) => s + simAllocations[c], 0);
    const remaining = 100 - newValue;
    const newAllocs = {...simAllocations, [changedCat]: newValue};

    if (currentOtherTotal > 0.01) {
      otherCats.forEach(cat => {
        newAllocs[cat] = (simAllocations[cat] / currentOtherTotal) * remaining;
      });
    } else {
      const share = remaining / otherCats.length;
      otherCats.forEach(cat => {
        newAllocs[cat] = share;
      });
    }

    setSimAllocations(newAllocs);
    setHasChanges(true);
  }, [simAllocations]);

  // ── Simulated values per category ─────────────────────────────────────
  const simCategoryValues = useMemo(() => {
    const vals = {};
    Object.keys(simAllocations).forEach(cat => {
      vals[cat] = (simAllocations[cat] / 100) * totalAssets;
    });
    return vals;
  }, [simAllocations, totalAssets]);

  // ── Current values per category ──────────────────────────────────────────
  const currentCategoryValues = useMemo(() => {
    const vals = {Equity: 0, Debt: 0, Commodity: 0, Other: 0};
    unifiedAssets.forEach(a => {
      vals[a.category] = (vals[a.category] || 0) + a.value;
    });
    return vals;
  }, [unifiedAssets]);

  // ── Simulated blended return ─────────────────────────────────────────────
  const simBlendedReturn = useMemo(() => {
    return Object.keys(simAllocations).reduce((s, cat) => {
      return s + (simAllocations[cat] / 100) * categoryReturns[cat];
    }, 0);
  }, [simAllocations, categoryReturns]);

  const currentBlendedReturn = useMemo(() => {
    return Object.keys(currentAllocations).reduce((s, cat) => {
      return s + (currentAllocations[cat] / 100) * categoryReturns[cat];
    }, 0);
  }, [currentAllocations, categoryReturns]);

  // ── Projections: 5y to 30y (both current and simulated) ─────────────────
  const projectionTimeline = [5, 10, 15, 20, 25, 30];
  const projections = useMemo(() => {
    return projectionTimeline.map(years => {
      const simValue = Object.keys(simAllocations).reduce((s, cat) => {
        const principal = (simAllocations[cat] / 100) * totalAssets;
        return s + principal * Math.pow(1 + categoryReturns[cat] / 100, years);
      }, 0);
      const currentValue = Object.keys(currentAllocations).reduce((s, cat) => {
        const principal = (currentAllocations[cat] / 100) * totalAssets;
        return s + principal * Math.pow(1 + categoryReturns[cat] / 100, years);
      }, 0);
      return {
        years,
        simValue,
        currentValue,
        diff: simValue - currentValue
      };
    });
  }, [simAllocations, currentAllocations, totalAssets, categoryReturns]);

  // ── Chart: Allocation Pie ────────────────────────────────────────────────
  const pieChartOption = useMemo(() => ({
    tooltip: {
      trigger: 'item',
      formatter: (p) => `${p.name}<br/>Allocation: <b>${p.value.toFixed(1)}%</b><br/>Value: <b>${formatLakh((p.value / 100) * totalAssets)}</b>`
    },
    legend: {show: false},
    series: [{
      type: 'pie',
      radius: ['45%', '72%'],
      label: {
        show: true,
        formatter: (p) => `${p.name}\n${p.value.toFixed(0)}%`,
        fontSize: 11,
        fontWeight: 'bold'
      },
      data: Object.entries(simAllocations)
        .filter(([, v]) => v > 0)
        .map(([name, value]) => ({
          name,
          value,
          itemStyle: {color: CATEGORY_COLORS[name]?.hex || '#ccc'}
        }))
    }]
  }), [simAllocations, totalAssets]);

  // ── Chart: Projection Bar ────────────────────────────────────────────────
  const projectionChartOption = useMemo(() => ({
    tooltip: {
      trigger: 'axis',
      formatter: (params) => {
        const yr = params[0].axisValue;
        return `<b>${yr} Years</b><br/>
          Current Mix: <b>${formatLakh(params[0].value)}</b><br/>
          Simulated Mix: <b>${formatLakh(params[1].value)}</b>`;
      }
    },
    legend: {
      data: ['Current Mix', 'Simulated Mix'],
      bottom: 0
    },
    xAxis: {
      type: 'category',
      data: projectionTimeline.map(y => `${y}Y`)
    },
    yAxis: {
      type: 'value',
      axisLabel: {formatter: v => formatLakh(v)}
    },
    series: [
      {
        name: 'Current Mix',
        type: 'bar',
        barGap: '5%',
        data: projections.map(p => Math.round(p.currentValue)),
        itemStyle: {color: '#94a3b8', borderRadius: [4, 4, 0, 0]}
      },
      {
        name: 'Simulated Mix',
        type: 'bar',
        data: projections.map(p => Math.round(p.simValue)),
        itemStyle: {color: '#19b48d', borderRadius: [4, 4, 0, 0]}
      }
    ],
    grid: {left: '5%', right: '3%', top: '10%', bottom: '15%', containLabel: true}
  }), [projections]);

  const returnDiff = simBlendedReturn - currentBlendedReturn;

  return (
    <motion.div initial={{opacity: 0}} animate={{opacity: 1}} className="max-w-7xl mx-auto pb-20 px-4">
      {/* ── Header ── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-10 gap-4">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 rounded-xl bg-[#19b48d] flex items-center justify-center shadow-lg shadow-[#19b48d]/20">
            <SafeIcon icon={FiTrendingUp} className="text-white text-xl" />
          </div>
          <div>
            <h2 className="text-3xl font-black text-gray-900">What-If Simulator</h2>
            <p className="text-gray-500 font-medium">Rebalance your portfolio and see real-time impact</p>
          </div>
        </div>
        {hasChanges && (
          <motion.div
            initial={{opacity: 0, x: 20}}
            animate={{opacity: 1, x: 0}}
            className="flex items-center space-x-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-xl"
          >
            <SafeIcon icon={FiAlertCircle} className="text-amber-500" />
            <span className="text-sm font-bold text-amber-700">Simulated allocation active</span>
          </motion.div>
        )}
      </div>

      {/* ── Net Worth Summary Bar ── */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        {[
          {label: 'Total Assets', value: totalAssets, color: 'text-emerald-600', bg: 'bg-emerald-50'},
          {label: 'Total Liabilities', value: totalLiabilities, color: 'text-red-500', bg: 'bg-red-50'},
          {label: 'Net Worth', value: currentNetWorth, color: 'text-gray-900', bg: 'bg-gray-900', dark: true}
        ].map(({label, value, color, bg, dark}) => (
          <div key={label} className={`rounded-2xl p-5 ${dark ? 'bg-gray-900' : bg + ' border border-gray-100'}`}>
            <p className={`text-xs font-black uppercase tracking-widest mb-1 ${dark ? 'text-white/50' : 'text-gray-400'}`}>{label}</p>
            <p className={`text-2xl font-black ${dark ? 'text-white' : color}`}>{formatLakh(value)}</p>
          </div>
        ))}
      </div>

      {/* ── Main Grid ── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-10">
        {/* ── LEFT: Sliders ── */}
        <div className="lg:col-span-7 bg-white rounded-3xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiSliders} className="text-[#19b48d]" />
              <h3 className="font-extrabold text-gray-900 uppercase tracking-tight">Proportional Rebalancer</h3>
            </div>
            <div className="flex items-center space-x-3">
              <div className="text-right">
                <p className="text-[10px] font-black text-gray-400 uppercase">Blended Return</p>
                <p className={`text-sm font-black ${returnDiff >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                  {simBlendedReturn.toFixed(2)}% p.a.
                  {hasChanges && (
                    <span className="ml-1 text-[10px]">
                      ({returnDiff >= 0 ? '+' : ''}{returnDiff.toFixed(2)}%)
                    </span>
                  )}
                </p>
              </div>
              <button
                onClick={() => {
                  setSimAllocations({...currentAllocations});
                  setHasChanges(false);
                }}
                className="px-3 py-1.5 bg-white border border-gray-200 text-gray-500 rounded-lg hover:bg-gray-100 transition-all font-bold text-[10px] uppercase flex items-center gap-1"
              >
                <SafeIcon icon={FiRefreshCw} />
                Reset
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Total check */}
            <div className="flex items-center justify-between px-4 py-3 bg-gray-50 rounded-xl border border-gray-100">
              <span className="text-xs font-black text-gray-400 uppercase tracking-widest">Total Allocation</span>
              <span className={`text-sm font-black ${Math.abs(Object.values(simAllocations).reduce((s, v) => s + v, 0) - 100) < 0.5 ? 'text-emerald-600' : 'text-red-500'}`}>
                {Object.values(simAllocations).reduce((s, v) => s + v, 0).toFixed(1)}%
                {Math.abs(Object.values(simAllocations).reduce((s, v) => s + v, 0) - 100) < 0.5 ? ' ✓' : ' (adjust sliders)'}
              </span>
            </div>

            {Object.keys(simAllocations).map(cat => {
              const colors = CATEGORY_COLORS[cat];
              const simVal = simCategoryValues[cat];
              const curVal = currentCategoryValues[cat];
              const valDiff = simVal - curVal;
              const allocDiff = simAllocations[cat] - currentAllocations[cat];

              return (
                <motion.div key={cat} layout className="space-y-3">
                  {/* Category Header */}
                  <div className="flex justify-between items-start">
                    <div className="flex items-center space-x-2">
                      <div className={`w-3 h-3 rounded-full ${colors.bg}`} />
                      <div>
                        <span className="text-sm font-black text-gray-700 uppercase tracking-tight">{cat}</span>
                        <div className="flex items-center gap-2 mt-0.5">
                          <ReturnBadge value={categoryReturns[cat]} />
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-2 justify-end">
                        <span className="text-3xl font-black text-gray-900 leading-none">
                          {simAllocations[cat].toFixed(1)}%
                        </span>
                        {hasChanges && <ChangeIndicator current={simAllocations[cat]} original={currentAllocations[cat]} />}
                      </div>
                    </div>
                  </div>

                  {/* Slider */}
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="0.5"
                    value={simAllocations[cat]}
                    onChange={e => handleSliderChange(cat, e.target.value)}
                    className="w-full h-3 rounded-xl appearance-none cursor-pointer"
                    style={{accentColor: colors.hex}}
                  />

                  {/* Value Cards */}
                  <div className="grid grid-cols-3 gap-2">
                    <div className={`${colors.light} rounded-xl p-3 border ${colors.border}`}>
                      <p className="text-[9px] font-black text-gray-400 uppercase mb-1">Current Value</p>
                      <p className={`text-sm font-black ${colors.text}`}>{formatLakh(curVal)}</p>
                    </div>
                    <div className="bg-[#19b48d]/5 rounded-xl p-3 border border-[#19b48d]/20">
                      <p className="text-[9px] font-black text-gray-400 uppercase mb-1">Simulated Value</p>
                      <p className="text-sm font-black text-[#19b48d]">{formatLakh(simVal)}</p>
                    </div>
                    <div className={`rounded-xl p-3 border ${valDiff >= 0 ? 'bg-blue-50 border-blue-100' : 'bg-red-50 border-red-100'}`}>
                      <p className="text-[9px] font-black text-gray-400 uppercase mb-1">Change</p>
                      <p className={`text-sm font-black flex items-center gap-0.5 ${valDiff >= 0 ? 'text-blue-600' : 'text-red-500'}`}>
                        {valDiff >= 0 ? <FiArrowUp /> : <FiArrowDown />}
                        {formatLakh(Math.abs(valDiff))}
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* ── RIGHT: Summary ── */}
        <div className="lg:col-span-5 flex flex-col space-y-6">
          {/* Pie Chart */}
          <div className="bg-white rounded-3xl shadow-sm border border-gray-200 p-6">
            <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4 flex items-center">
              <SafeIcon icon={FiPieChart} className="mr-2 text-[#19b48d]" />
              Simulated Allocation Mix
            </h4>
            <div className="h-52">
              <ReactECharts option={pieChartOption} style={{height: '100%'}} />
            </div>
          </div>

          {/* Blended Return Impact */}
          <div className="bg-white rounded-3xl shadow-sm border border-gray-200 p-6">
            <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4">Return Impact Analysis</h4>
            <div className="space-y-3">
              {Object.keys(simAllocations).map(cat => {
                const colors = CATEGORY_COLORS[cat];
                const contribution = (simAllocations[cat] / 100) * categoryReturns[cat];
                return (
                  <div key={cat} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-2.5 h-2.5 rounded-full ${colors.bg}`} />
                      <span className="text-xs font-bold text-gray-600">{cat}</span>
                      <span className="text-[10px] text-gray-400">({simAllocations[cat].toFixed(1)}%)</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-24 bg-gray-100 rounded-full h-1.5">
                        <div
                          className={`h-1.5 rounded-full ${colors.bg}`}
                          style={{width: `${(contribution / simBlendedReturn) * 100}%`}}
                        />
                      </div>
                      <span className="text-xs font-black text-gray-700 w-14 text-right">
                        {contribution.toFixed(2)}%
                      </span>
                    </div>
                  </div>
                );
              })}
              <div className="border-t pt-3 flex justify-between items-center">
                <span className="text-xs font-black text-gray-700 uppercase">Total Blended Return</span>
                <span className="text-lg font-black text-[#19b48d]">{simBlendedReturn.toFixed(2)}% p.a.</span>
              </div>
            </div>
          </div>

          {/* Current vs Sim summary */}
          {hasChanges && (
            <motion.div
              initial={{opacity: 0, y: 10}}
              animate={{opacity: 1, y: 0}}
              className="bg-gray-900 rounded-3xl p-6 text-white"
            >
              <h4 className="text-white/50 text-[10px] font-black uppercase tracking-widest mb-4">Rebalancing Summary</h4>
              <div className="space-y-3">
                {Object.keys(simAllocations).map(cat => {
                  const diff = simAllocations[cat] - currentAllocations[cat];
                  if (Math.abs(diff) < 0.1) return null;
                  return (
                    <div key={cat} className="flex items-center justify-between">
                      <span className="text-white/70 text-sm font-bold">{cat}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-white/50 text-xs">{currentAllocations[cat].toFixed(1)}%</span>
                        <span className="text-white/30 text-xs">→</span>
                        <span className="text-white text-sm font-black">{simAllocations[cat].toFixed(1)}%</span>
                        <span className={`text-xs font-black px-2 py-0.5 rounded-full ${diff > 0 ? 'bg-blue-500/20 text-blue-400' : 'bg-red-500/20 text-red-400'}`}>
                          {diff > 0 ? '+' : ''}{diff.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  );
                })}
                <div className="border-t border-white/10 pt-3 flex justify-between">
                  <span className="text-white/60 text-xs font-bold">Return change</span>
                  <span className={`text-sm font-black ${returnDiff >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {returnDiff >= 0 ? '+' : ''}{returnDiff.toFixed(2)}% p.a.
                  </span>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* ── Projection Timeline Cards ── */}
      <div className="mb-10">
        <h3 className="text-xl font-black text-gray-900 mb-6 flex items-center">
          <SafeIcon icon={FiCalendar} className="mr-2 text-[#19b48d]" />
          Net Worth Projection Timeline
          {hasChanges && (
            <span className="ml-3 text-sm font-bold text-amber-600 bg-amber-50 px-3 py-1 rounded-full border border-amber-200">
              Simulated vs Current
            </span>
          )}
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          {projections.map((p, i) => {
            const isPositive = p.diff >= 0;
            return (
              <motion.div
                key={p.years}
                initial={{opacity: 0, y: 20}}
                animate={{opacity: 1, y: 0}}
                transition={{delay: i * 0.07}}
                className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm hover:shadow-md hover:border-[#19b48d]/30 transition-all group"
              >
                <p className="text-[10px] font-black text-[#19b48d] uppercase mb-2">After {p.years} Years</p>
                <p className="text-base font-black text-gray-900 group-hover:text-[#19b48d] transition-colors leading-tight">
                  {formatLakh(p.simValue)}
                </p>
                <div className="mt-2 text-[10px] font-bold text-gray-400">
                  <span className="bg-gray-100 px-2 py-0.5 rounded">
                    {totalAssets > 0 ? (p.simValue / totalAssets).toFixed(1) : '0'}x
                  </span>
                </div>
                {hasChanges && (
                  <div className={`mt-2 text-[10px] font-black flex items-center gap-0.5 ${isPositive ? 'text-emerald-600' : 'text-red-500'}`}>
                    {isPositive ? <FiArrowUp /> : <FiArrowDown />}
                    {formatLakh(Math.abs(p.diff))}
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Projection Bar Chart */}
        <div className="bg-white rounded-3xl shadow-sm border border-gray-200 p-6">
          <h4 className="text-sm font-black text-gray-700 uppercase tracking-tight mb-4">
            Current Mix vs Simulated Mix — Growth Comparison
          </h4>
          <div className="h-64">
            <ReactECharts option={projectionChartOption} style={{height: '100%'}} />
          </div>
        </div>
      </div>

      {/* ── Assets Table ── */}
      <div className="bg-white rounded-3xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
          <h3 className="font-extrabold text-gray-900 flex items-center uppercase text-sm tracking-tight">
            <SafeIcon icon={FiBox} className="mr-2 text-[#19b48d]" />
            Portfolio Components
          </h3>
          <div className="flex gap-2">
            <button
              onClick={() => setShowLiabilityModal(true)}
              className="px-4 py-2 bg-red-50 text-red-600 text-xs font-black rounded-xl hover:bg-red-100 transition-all flex items-center uppercase tracking-wider border border-red-100"
            >
              <SafeIcon icon={FiPlus} className="mr-1" />
              Add Liability
            </button>
            <button
              onClick={() => setShowAssetModal(true)}
              className="px-4 py-2 bg-[#19b48d] text-white text-xs font-black rounded-xl hover:bg-[#16a085] transition-all flex items-center uppercase tracking-wider"
            >
              <SafeIcon icon={FiPlus} className="mr-1" />
              Add Asset
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-gray-50 text-[10px] uppercase font-black tracking-widest text-gray-400 border-b border-gray-100">
                <th className="px-6 py-4">Asset</th>
                <th className="px-6 py-4">Category</th>
                <th className="px-6 py-4 text-right">Current Value</th>
                <th className="px-6 py-4 text-center">ROI (% p.a.)</th>
                <th className="px-6 py-4 text-right">10Y Projection</th>
                <th className="px-6 py-4 text-right">30Y Projection</th>
                <th className="px-6 py-4 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {unifiedAssets.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-400 font-bold">
                    No assets found. Add assets in the Details section or use "Add Asset" above.
                  </td>
                </tr>
              )}
              {unifiedAssets.map(asset => {
                const fv10 = asset.value * Math.pow(1 + asset.expectedReturn / 100, 10);
                const fv30 = asset.value * Math.pow(1 + asset.expectedReturn / 100, 30);
                const colors = CATEGORY_COLORS[asset.category];
                const hasOverride = !asset.isManual && (asset.category !== asset.originalCategory || assetOverrides[asset.id]?.expectedReturn !== undefined);

                return (
                  <tr key={asset.id} className="hover:bg-gray-50/50 transition-colors group">
                    <td className="px-6 py-4">
                      <p className="font-bold text-gray-900 text-sm">{asset.name}</p>
                      {!asset.isManual && (
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-[10px] text-blue-500 font-bold uppercase">Linked to profile</span>
                          {hasOverride && (
                            <span className="text-[10px] text-amber-600 font-bold uppercase bg-amber-50 px-2 py-0.5 rounded">
                              Modified
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-lg text-[10px] font-black uppercase ${colors.light} ${colors.text}`}>
                        {asset.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right font-black text-gray-900 text-sm">
                      {formatCurrency(asset.value)}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-sm font-black text-gray-700">{asset.expectedReturn}%</span>
                    </td>
                    <td className="px-6 py-4 text-right font-black text-[#19b48d] text-sm">{formatLakh(fv10)}</td>
                    <td className="px-6 py-4 text-right font-black text-purple-600 text-sm">{formatLakh(fv30)}</td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => setEditingAsset(asset)}
                          className="p-2 text-blue-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                          title="Edit Category & ROI"
                        >
                          <SafeIcon icon={FiEdit2} />
                        </button>
                        {asset.isManual && (
                          <button
                            onClick={() => dispatch({type: 'DELETE_MANUAL_ASSET', payload: asset.id})}
                            className="p-2 text-red-300 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                            title="Delete Asset"
                          >
                            <SafeIcon icon={FiTrash2} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {unifiedAssets.length > 0 && (
              <tfoot>
                <tr className="bg-gray-900 text-white">
                  <td className="px-6 py-4 font-black text-sm uppercase tracking-wider" colSpan={2}>
                    Portfolio Total
                  </td>
                  <td className="px-6 py-4 text-right font-black text-white">{formatLakh(totalAssets)}</td>
                  <td className="px-6 py-4 text-center font-black text-[#19b48d]">{currentBlendedReturn.toFixed(1)}%</td>
                  <td className="px-6 py-4 text-right font-black text-[#19b48d]">
                    {formatLakh(unifiedAssets.reduce((s, a) => s + a.value * Math.pow(1 + a.expectedReturn / 100, 10), 0))}
                  </td>
                  <td className="px-6 py-4 text-right font-black text-purple-400">
                    {formatLakh(unifiedAssets.reduce((s, a) => s + a.value * Math.pow(1 + a.expectedReturn / 100, 30), 0))}
                  </td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* ── Liabilities Table ── */}
      {(Object.values(userProfile.liabilities).some(v => Number(v) > 0) || manualLiabilities.length > 0) && (
        <div className="bg-white rounded-3xl shadow-sm border border-gray-200 overflow-hidden mt-6">
          <div className="p-6 border-b border-gray-100 bg-red-50/30">
            <h3 className="font-extrabold text-gray-900 flex items-center uppercase text-sm tracking-tight">
              <SafeIcon icon={FiCreditCard} className="mr-2 text-red-500" />
              Liabilities
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-red-50/50 text-[10px] uppercase font-black tracking-widest text-gray-400 border-b border-gray-100">
                  <th className="px-6 py-4">Liability</th>
                  <th className="px-6 py-4 text-right">Amount</th>
                  <th className="px-6 py-4 text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {Object.entries(userProfile.liabilities)
                  .filter(([, v]) => Number(v) > 0)
                  .map(([key, val]) => (
                    <tr key={key} className="hover:bg-red-50/20">
                      <td className="px-6 py-4 font-bold text-gray-700 text-sm capitalize">
                        {key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}
                        <span className="ml-2 text-[10px] text-blue-500 font-bold uppercase">Profile</span>
                      </td>
                      <td className="px-6 py-4 text-right font-black text-red-500">{formatCurrency(Number(val))}</td>
                      <td />
                    </tr>
                  ))}
                {manualLiabilities.map(l => (
                  <tr key={l.id} className="hover:bg-red-50/20 group">
                    <td className="px-6 py-4 font-bold text-gray-700 text-sm">{l.name}</td>
                    <td className="px-6 py-4 text-right font-black text-red-500">{formatCurrency(Number(l.value))}</td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => dispatch({type: 'DELETE_MANUAL_LIABILITY', payload: l.id})}
                        className="p-2 text-red-300 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                      >
                        <SafeIcon icon={FiTrash2} />
                      </button>
                    </td>
                  </tr>
                ))}
                <tr className="bg-red-900 text-white">
                  <td className="px-6 py-4 font-black uppercase text-sm">Total Liabilities</td>
                  <td className="px-6 py-4 text-right font-black">{formatLakh(totalLiabilities)}</td>
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Modals ── */}
      <AnimatePresence>
        {showAssetModal && <AssetModal onClose={() => setShowAssetModal(false)} dispatch={dispatch} />}
        {showLiabilityModal && <LiabilityModal onClose={() => setShowLiabilityModal(false)} dispatch={dispatch} />}
        {editingAsset && (
          <AssetEditModal
            asset={editingAsset}
            onClose={() => setEditingAsset(null)}
            onSave={handleAssetEditSave}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ─── ASSET MODAL ──────────────────────────────────────────────────────────────
function AssetModal({onClose, dispatch}) {
  const [form, setForm] = useState({
    name: '',
    category: 'Equity',
    value: '',
    expectedReturn: '12'
  });

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{scale: 0.9, opacity: 0}}
        animate={{scale: 1, opacity: 1}}
        className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8"
      >
        <div className="flex justify-between items-center mb-8">
          <h3 className="text-xl font-black text-gray-900 uppercase">Add Asset</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-all">
            <SafeIcon icon={FiX} />
          </button>
        </div>
        <form
          onSubmit={e => {
            e.preventDefault();
            dispatch({type: 'ADD_MANUAL_ASSET', payload: form});
            onClose();
          }}
          className="space-y-5"
        >
          <div className="space-y-1">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Asset Name</label>
            <input
              required
              type="text"
              value={form.name}
              onChange={e => setForm({...form, name: e.target.value})}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#19b48d] outline-none font-bold"
              placeholder="e.g. BTC, Rental Flat"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Category</label>
              <select
                value={form.category}
                onChange={e => setForm({...form, category: e.target.value})}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl font-bold outline-none"
              >
                <option>Equity</option>
                <option>Debt</option>
                <option>Commodity</option>
                <option>Other</option>
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Return (% p.a.)</label>
              <input
                required
                type="number"
                step="0.1"
                value={form.expectedReturn}
                onChange={e => setForm({...form, expectedReturn: e.target.value})}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl font-bold outline-none"
              />
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Current Value (₹)</label>
            <input
              required
              type="number"
              value={form.value}
              onChange={e => setForm({...form, value: e.target.value})}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl font-bold outline-none"
              placeholder="0"
            />
          </div>
          <button
            type="submit"
            className="w-full py-4 bg-[#19b48d] text-white font-black rounded-xl shadow-lg shadow-[#19b48d]/20 hover:bg-[#16a085] transition-all uppercase tracking-widest"
          >
            Add Asset
          </button>
        </form>
      </motion.div>
    </div>
  );
}

// ─── LIABILITY MODAL ──────────────────────────────────────────────────────────
function LiabilityModal({onClose, dispatch}) {
  const [form, setForm] = useState({
    name: '',
    value: '',
    interestRate: '9.5'
  });

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{scale: 0.9, opacity: 0}}
        animate={{scale: 1, opacity: 1}}
        className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8"
      >
        <div className="flex justify-between items-center mb-8">
          <h3 className="text-xl font-black text-gray-900 uppercase">Add Liability</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-all">
            <SafeIcon icon={FiX} />
          </button>
        </div>
        <form
          onSubmit={e => {
            e.preventDefault();
            dispatch({type: 'ADD_MANUAL_LIABILITY', payload: form});
            onClose();
          }}
          className="space-y-5"
        >
          <div className="space-y-1">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Liability Name</label>
            <input
              required
              type="text"
              value={form.name}
              onChange={e => setForm({...form, name: e.target.value})}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 outline-none font-bold"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Amount (₹)</label>
              <input
                required
                type="number"
                value={form.value}
                onChange={e => setForm({...form, value: e.target.value})}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl font-bold outline-none"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Interest Rate (%)</label>
              <input
                required
                type="number"
                step="0.1"
                value={form.interestRate}
                onChange={e => setForm({...form, interestRate: e.target.value})}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl font-bold outline-none"
              />
            </div>
          </div>
          <button
            type="submit"
            className="w-full py-4 bg-red-500 text-white font-black rounded-xl shadow-lg shadow-red-500/20 hover:bg-red-600 transition-all uppercase tracking-widest"
          >
            Add Liability
          </button>
        </form>
      </motion.div>
    </div>
  );
}

export default InvestmentPlanning;